import { DialogActions, DialogContent, Stack } from '@mui/material'
import { GridRowSelectionModel, useGridApiRef } from '@mui/x-data-grid-pro'
import { useMemo, useState } from 'react'
import { Mutation_Root } from 'src/@types/graphql'
import { updateKapMeasureFactsheetIdMutation } from 'src/screens/kap/program/details/measures/kapMeasuresQueries'
import { FactsheetFilterModalCard } from 'src/screens/shared/factsheet-relation/FactsheetFilterModalCard'
import { RelatedFactsheetsGrid } from 'src/screens/shared/factsheet-relation/RelatedFactsheetsGrid'
import { useFetchRelatedFactsheets } from 'src/screens/shared/factsheet-relation/useFetchRelatedFactsheets'
import { PrimaryButton, SecondaryButton } from 'src/shared/button/Buttons'
import { LEVEL, LEVEL_TYPE, LEVELS, MODULE_TYPES } from 'src/shared/constants/constants'
import { HelpAndInstructions } from 'src/shared/presentation/HelpAndInstructions'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { useClient } from 'urql'

interface Props {
  onCancel: () => void
  onSuccess: () => void
  measureId: number
  modules: MODULE_TYPES[]
  level: string
}

export const KapLinkRelatedFactsheetModalDialog = ({ onSuccess, onCancel, measureId, modules, level }: Props) => {
  const gridRef = useGridApiRef()
  const urqlClient = useClient()
  const notificationService = useNotificationService()

  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([])

  const levelsForFiltering = useMemo(() => (level === LEVEL.POLICY ? [...LEVELS] : [level]), [level])

  const { filteredFactsheets, fetchData } = useFetchRelatedFactsheets({
    modules,
    levels: levelsForFiltering as LEVEL_TYPE[],
  })

  const onSubmit = async () => {
    const rows = gridRef.current?.getSelectedRows()
    const selectedFactsheet = rows?.get(selectionModel[0])

    const { data, error } = await urqlClient
      .mutation<{
        update_kap_measure: Mutation_Root['update_kap_measure']
      }>(updateKapMeasureFactsheetIdMutation, {
        measureId: measureId,
        factsheetId: selectedFactsheet?.id,
      })
      .toPromise()

    if (error || data?.update_kap_measure?.affected_rows !== 1) {
      notificationService.operationFailed()
    } else {
      notificationService.changesSaved()
      onSuccess()
    }
  }

  return (
    <>
      <DialogContent>
        {filteredFactsheets && (
          <>
            <HelpAndInstructions labelKey="label.help.kap.measure.related.factsheet" defaultExpansion />
            <Stack spacing={2}>
              <FactsheetFilterModalCard
                modules={modules}
                levels={levelsForFiltering as LEVEL_TYPE[]}
                fetchData={fetchData}
              />
              <RelatedFactsheetsGrid
                factsheets={filteredFactsheets}
                selectionModel={selectionModel}
                setSelectionModel={setSelectionModel}
                gridRef={gridRef as any}
              />
            </Stack>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <SecondaryButton messageKey={'button.cancel'} onClick={onCancel} />
        <PrimaryButton messageKey={'button.link.factsheet'} disabled={!selectionModel.length} onClick={onSubmit} />
      </DialogActions>
    </>
  )
}
